/**
 * /ai-image-generator — страница генератора картинок по описанию.
 *
 * Что здесь, и почему так (исследование конкурентов 24.09.2026):
 *   • цена — ДО нажатия, в деньгах и токенах, для выбранной модели × качества ×
 *     числа вариантов (ни один конкурент не показывает деньги на кнопке);
 *   • «Авто» выбирает модель по запросу теми же правилами, что и сервер
 *     (ImageModelRegistry::autoPick) — страница только ПОКАЗЫВАЕТ выбор;
 *   • надпись — отдельным полем, уходит дословно;
 *   • размер площадки: модель получает формат, точные пиксели делает эта
 *     страница при скачивании (обрезка по центру + масштаб);
 *   • сравнение моделей — одним нажатием, колонками;
 *   • частичная выдача и отказ модели называются словами, с возвратом.
 * ⛔ Ни sticky, ни автопрокрутки (правило Фарида): новые результаты ложатся в
 * начало сцены, видимость — scrollIntoView({block:'nearest'}) и только если
 * карточка вне экрана.
 */
import { canvasLimits } from '../canvas-limits.js';

const CFG = window.__AIG || {};
const I = CFG.i18n || {};
const U = CFG.urls || {};
const SLUG = 'ai-image-generator';

const $ = (id) => document.getElementById(id);
const show = (el, on) => { if (el) { if (on) el.removeAttribute('hidden'); else el.setAttribute('hidden', ''); } };
const fmt = (str, map) => String(str || '').replace(/\{(\w+)\}/g, (m, k) => (map && map[k] != null ? String(map[k]) : m));
const num = (n) => String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const csrf = () => (document.querySelector('meta[name="csrf-token"]') || {}).content || '';
const ae = (f, extra) => { try { window.__ae && window.__ae('feature_use', Object.assign({ tool_slug: SLUG, f: f }, extra || {})); } catch (_) { /* телеметрия не должна ронять тул */ } };
const stage = (n, d) => { try { window.__stage && window.__stage(n, d); } catch (_) { /* то же */ } };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const S = {
    boot: null, gen: null, models: [], authed: false, balance: null, live: false,
    model: 'auto', size: 'square', res: '1K', count: 1, style: 'none', transparent: false,
    refs: [], compare: false, cmp: [], busy: 0,
    runs: [], histBefore: 0, histDone: false, lbList: [], lbIdx: 0,
    // 'draw' — нарисовать с нуля, 'edit' — изменить своё фото. Режим меняет
    // подписи, порядок блоков и то, за чем идёт размер; запрос и цена те же.
    mode: 'draw',
    // Размер в режиме правки идёт за ФОТО, пока человек не выбрал его сам.
    sizeTouched: false, sizeAuto: false, sizeOwn: null,
    // «Фото не трогать — рисовать только вокруг»: поля в % меньшей стороны и
    // загруженный холст (переиспользуется, пока фото, размер и поля те же).
    around: false, margin: 10, aroundRef: null,
    // «Совместное фото»: выбранная поза (hug | side | hands | young) или null.
    together: null,
};
// Обещание первого ответа bootstrap: до него страница не знает ни моделей, ни
// того, вошёл ли человек. Назначается в самом низу, до провязки событий.
let BOOT = null;

/* ─────────────────────────── настройки между визитами ─────────────────────── */
const TS = () => window.__toolSettings;
function loadPrefs() {
    const ts = TS();
    if (!ts || !ts.load) return;
    try {
        S.model = ts.load(SLUG, 'model', 'auto') || 'auto';
        S.size = ts.load(SLUG, 'size', S.size) || S.size;
        S.res = ts.load(SLUG, 'res', S.res) || S.res;
        S.count = Math.max(1, Math.min(4, parseInt(ts.load(SLUG, 'count', 1), 10) || 1));
        S.style = ts.load(SLUG, 'style', 'none') || 'none';
        S.mode = ts.load(SLUG, 'mode', 'draw') === 'edit' ? 'edit' : 'draw';
        // Помнятся поля, но не сам переключатель: забытое «только вокруг» молча
        // превратило бы следующую «замену фона» в рисование по краям.
        S.margin = Math.max(0, Math.min(30, parseInt(ts.load(SLUG, 'margin', 10), 10) || 0));
    } catch (_) { /* повреждённое сохранение — умолчания */ }
}
function savePref(k, v) { try { TS() && TS().save && TS().save(SLUG, k, v); } catch (_) { /* не критично */ } }

/* ─────────────────────────── модели и цена ────────────────────────────────── */
const modelOf = (slug) => S.models.find((m) => m.slug === slug) || null;
const hasText = () => ($('aigText').value || '').trim() !== '';
// `pending` — фото гостя: оно выбрано и видно, но на сервер уедет после входа.
// В счёт идёт сразу, иначе «Авто» и цена показывали бы запуск без фото.
const refCount = () => S.refs.filter((r) => r.id || r.pending).length;

/** «Авто» — те же правила, что у сервера (config ai-image-models.auto). */
function autoPick() {
    const rules = (S.gen && S.gen.auto) || {};
    const n = refCount(), refs = n > 0, text = hasText(), tr = !!S.transparent;
    // Число, а не «есть/нет»: 11 образцов не помещаются в Nano Banana 2.1 (10),
    // и «Авто» уходит туда, где помещаются (GPT Image 2.5 — 16). Зеркало
    // ImageModelRegistry::autoPick на сервере.
    const fits = (m) => !refs || m.refs >= n;
    const tryList = [];
    if (tr) tryList.push(rules.transparent);
    if (text) tryList.push(rules.text);
    if (refs) tryList.push(rules.refs);
    tryList.push(rules.default);
    for (const slug of tryList) {
        const m = modelOf(slug);
        if (m && fits(m) && (!tr || m.transparent)) return m;
    }
    return S.models.find(fits) || S.models[0] || null;
}
/** Сколько образцов можно добавить: у «Авто» — сколько примет хоть одна модель. */
function refCap() {
    if (S.model === 'auto') return S.models.reduce((mx, m) => Math.max(mx, m.refs || 0), 0) || 14;
    const m = effModel();
    return (m && m.refs > 0) ? m.refs : 0;
}
const effModel = () => (S.model === 'auto' ? autoPick() : modelOf(S.model));
function resFor(m) {
    if (!m) return S.res;
    return m.resolutions.includes(S.res) ? S.res : (m.default_res || m.resolutions[0] || '1K');
}
function perImage(m, res) {
    if (!m) return 0;
    let p = (m.prices && m.prices[res]) || 0;
    const n = refCount();
    if (m.ref_fee && n > m.ref_fee.free) p += m.ref_fee.tokens * (n - m.ref_fee.free);
    return p;
}
function runCost(m) { return perImage(m, resFor(m)) * S.count; }
function totalCost() {
    if (S.compare && S.cmp.length) return S.cmp.reduce((a, slug) => a + runCost(modelOf(slug)), 0);
    return runCost(effModel());
}
function money(tokens) {
    const tp = window.__tokenPrice;
    const m = tp && tp.rate ? tp.money(tokens) : '';
    return m || fmt(I.cost_tokens, { n: num(tokens) });
}
/** Деньги без знака «≈» — для шаблонов, где «≈» уже стоит в тексте. */
const moneyBare = (tokens) => money(tokens).replace(/^[≈~]\s*/, '');

/* ─────────────────────────── отрисовка органов ────────────────────────────── */
function sizeLabel(key) {
    const z = (S.allSizes && S.allSizes[key]) || {};
    const name = I['size_' + key] || key;
    return z.px ? name + ' · ' + z.px[0] + '×' + z.px[1] : name;
}
function roleLabel(m) { return I['role_' + m.role] || ''; }
function modelFacts(m) {
    const out = [];
    (m.best || []).forEach((b) => { if (I['best_' + b]) out.push(I['best_' + b]); });
    if (m.text === 'latin') out.push(I.text_latin_only);
    if (m.refs > 0) out.push(fmt(I.refs_upto, { n: m.refs }));
    return out.join(' · ');
}
function opt(pop, html, selected, onPick, disabled) {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'aig-opt'; b.setAttribute('role', 'option');
    b.setAttribute('aria-selected', selected ? 'true' : 'false');
    if (disabled) b.disabled = true;
    b.innerHTML = html;
    b.addEventListener('click', () => { onPick(); try { pop.hidePopover(); } catch (_) { pop.setAttribute('data-pop-open', 'false'); } paintAll(); });
    pop.appendChild(b);
    return b;
}

function paintModelPop() {
    const pop = $('aigPopModel'); pop.textContent = '';
    const auto = autoPick();
    opt(pop, '<span class="aig-opt-main"><span class="aig-opt-name">' + esc(I.model_auto) + ' <i>' + esc(auto ? fmt(I.model_auto_now, { model: auto.name }) : '') + '</i></span>'
        + '<span class="aig-opt-facts">' + esc(I.model_auto_desc) + '</span></span>'
        + '<span class="aig-opt-price">' + esc(money(perImage(auto, resFor(auto)))) + '</span>',
        S.model === 'auto', () => { S.model = 'auto'; savePref('model', 'auto'); ae('aig_model', { m: 'auto' }); });
    S.models.forEach((m) => {
        const blocked = refCount() > (m.refs || 0);
        opt(pop, '<span class="aig-opt-main"><span class="aig-opt-name">' + esc(m.name) + ' <i>' + esc(m.vendor) + ' · ' + esc(roleLabel(m)) + '</i></span>'
            + '<span class="aig-opt-facts">' + esc(modelFacts(m)) + '</span></span>'
            + '<span class="aig-opt-price">' + esc(money(perImage(m, resFor(m)))) + '</span>',
            S.model === m.slug, () => { S.model = m.slug; savePref('model', m.slug); ae('aig_model', { m: m.slug }); }, blocked);
    });
}
function paintSizePop() {
    const pop = $('aigPopSize'); pop.textContent = '';
    const sizes = (S.gen && S.gen.sizes) || {};
    const groups = {};
    Object.keys(sizes).forEach((k) => { const g = sizes[k].group || 'basic'; (groups[g] = groups[g] || []).push(k); });
    ['basic', 'market', 'social', 'screen', 'print'].forEach((g) => {
        if (!groups[g]) return;
        const h = document.createElement('div'); h.className = 'aig-pop-h'; h.textContent = I['group_' + g] || g; pop.appendChild(h);
        groups[g].forEach((k) => {
            const z = sizes[k];
            const facts = z.px ? fmt(I.size_exact, { w: z.px[0], h: z.px[1] }) + ' · ' + z.aspect : z.aspect;
            opt(pop, '<span class="aig-opt-main"><span class="aig-opt-name">' + esc(I['size_' + k] || k) + '</span><span class="aig-opt-facts">' + esc(facts) + '</span></span>',
                S.size === k, () => { S.size = k; S.sizeTouched = true; S.sizeAuto = false; S.sizeOwn = null; savePref('size', k); ae('aig_size', { s: k }); });
        });
    });
}
function paintResPop() {
    const pop = $('aigPopRes'); pop.textContent = '';
    const m = effModel(); if (!m) return;
    m.resolutions.forEach((r) => {
        opt(pop, '<span class="aig-opt-main"><span class="aig-opt-name">' + esc(I['res_' + r] || r) + '</span></span><span class="aig-opt-price">' + esc(money(perImage(m, r))) + '</span>',
            resFor(m) === r, () => { S.res = r; savePref('res', r); ae('aig_res', { r: r }); });
    });
}
function paintCountPop() {
    const pop = $('aigPopCount'); pop.textContent = '';
    const row = document.createElement('div'); row.className = 'aig-countrow'; pop.appendChild(row);
    const max = (S.gen && S.gen.max_count) || 4;
    const m = effModel();
    for (let n = 1; n <= max; n++) {
        const b = opt(row, '<span class="aig-opt-name">' + n + '</span><span class="aig-opt-facts">' + esc(money(perImage(m, resFor(m)) * n)) + '</span>',
            S.count === n, () => { S.count = n; savePref('count', n); ae('aig_count', { n: n }); });
        b.addEventListener('click', () => { try { pop.hidePopover(); } catch (_) { /* уже закрыт */ } });
    }
}
function paintStylePop() {
    const pop = $('aigPopStyle'); pop.textContent = '';
    const grid = document.createElement('div'); grid.className = 'aig-stylegrid'; pop.appendChild(grid);
    ((S.gen && S.gen.styles) || ['none']).forEach((k) => {
        const b = opt(grid, '<span class="aig-opt-name">' + esc(I['style_' + k] || k) + '</span>', S.style === k,
            () => { S.style = k; savePref('style', k); ae('aig_style', { s: k }); });
        b.addEventListener('click', () => { try { pop.hidePopover(); } catch (_) { /* уже закрыт */ } });
    });
}
function paintBgPop() {
    const pop = $('aigPopBg'); pop.textContent = '';
    const m = S.model === 'auto' ? null : modelOf(S.model);
    const can = !m || m.transparent;
    opt(pop, '<span class="aig-opt-main"><span class="aig-opt-name">' + esc(I.bg_normal) + '</span></span>', !S.transparent, () => { S.transparent = false; });
    opt(pop, '<span class="aig-opt-main"><span class="aig-opt-name">' + esc(I.bg_transparent) + '</span><span class="aig-opt-facts">' + esc(I.bg_only_gpt) + '</span></span>',
        S.transparent, () => { S.transparent = true; ae('aig_transparent'); }, !can);
}
function paintCompare() {
    const box = $('aigCompare'); show(box, S.compare);
    const list = $('aigCompareList'); list.textContent = '';
    const max = (S.gen && S.gen.max_compare) || 3;
    S.models.forEach((m) => {
        const row = document.createElement('label'); row.className = 'aig-cmp-row';
        const cb = document.createElement('input'); cb.type = 'checkbox'; cb.value = m.slug; cb.checked = S.cmp.includes(m.slug);
        const blocked = refCount() > (m.refs || 0);
        cb.disabled = blocked || (!cb.checked && S.cmp.length >= max);
        cb.addEventListener('change', () => {
            if (cb.checked) { if (!S.cmp.includes(m.slug)) S.cmp.push(m.slug); } else { S.cmp = S.cmp.filter((s) => s !== m.slug); }
            paintAll();
        });
        const t = document.createElement('span'); t.textContent = m.name;
        const p = document.createElement('small'); p.textContent = money(runCost(m));
        row.appendChild(cb); row.appendChild(t); row.appendChild(p);
        list.appendChild(row);
    });
    $('aigCompareHint').textContent = I.compare_pick ? fmt(I.compare_pick, { max: max }) : '';
}

function paintKnobs() {
    const m = effModel();
    $('aigKnobModelV').textContent = S.model === 'auto' ? (I.model_auto + (m ? ' → ' + m.name : '')) : (m ? m.name : S.model);
    $('aigKnobSizeV').textContent = sizeLabel(S.size) + (S.sizeAuto && I.size_as_photo ? ' · ' + I.size_as_photo : '');
    $('aigKnobResV').textContent = I['res_' + resFor(m)] || resFor(m);
    $('aigKnobCountV').textContent = String(S.count);
    $('aigKnobStyleV').textContent = I['style_' + S.style] || S.style;
    $('aigKnobBgV').textContent = S.transparent ? I.bg_transparent : I.bg_normal;
    show($('aigKnobModel'), !S.compare);
    show($('aigKnobRes'), !!m && m.resolutions.length > 1);
    // Фон — там, где его можно сделать настоящим: у выбранной GPT Image или в «Авто».
    const bgOk = S.model === 'auto' || (m && m.transparent);
    show($('aigKnobBg'), bgOk && !S.compare);
    if (!bgOk && S.transparent) S.transparent = false;
}

function paintTextWarn() {
    const m = effModel(), w = $('aigTextWarn');
    const txt = ($('aigText').value || '').trim();
    // eslint-disable-next-line no-control-regex
    const nonLatin = /[^\x00-\x7F«»№—–…‘’“”]/.test(txt);
    const bad = !!(m && m.text === 'latin' && txt && nonLatin);
    w.textContent = bad ? fmt(I.text_latin_warn, { model: m.name }) : '';
    show(w, bad);
}

function paintChars() {
    const el = $('aigChars'), v = $('aigPrompt').value || '';
    const m = effModel();
    const max = Math.min((S.gen && S.gen.limits && S.gen.limits.prompt) || 2000, (m && m.prompt_max) || 2000);
    el.textContent = fmt(I.prompt_count, { n: v.length, max: max });
    el.classList.toggle('over', v.length > max);
}

function paintRefs() {
    const grid = $('aigRefs'); grid.textContent = '';
    S.refs.forEach((r, i) => {
        const cell = document.createElement('div'); cell.className = 'aig-ref' + (r.busy ? ' busy' : '');
        const img = document.createElement('img'); img.alt = ''; img.src = r.thumb; img.loading = 'lazy';
        const x = document.createElement('button'); x.type = 'button'; x.className = 'aig-ref-x'; x.textContent = '×';
        x.setAttribute('aria-label', I.refs_remove || '×');
        x.addEventListener('click', () => { S.refs.splice(i, 1); paintAll(); });
        cell.appendChild(img); cell.appendChild(x); grid.appendChild(cell);
    });
    show(grid, S.refs.length > 0);
    const m = effModel();
    const max = refCap() || 14;
    $('aigRefsSub').textContent = S.refs.length ? fmt(I.refs_sub_n, { n: S.refs.length, max: max }) : I.refs_sub_none;
    show($('aigConsentRow'), S.refs.length > 0);
}

function paintPrice() {
    const cost = totalCost();
    const m = effModel();
    $('aigCost').textContent = cost > 0 ? money(cost) : '';
    const sub = $('aigCostSub');
    if (S.compare) {
        sub.textContent = fmt(I.cost_tokens, { n: num(cost) }) + ' · ' + S.cmp.map((s) => (modelOf(s) || {}).name).filter(Boolean).join(' · ');
    } else if (m) {
        sub.textContent = fmt(I.cost_line, { count: S.count, per: num(perImage(m, resFor(m))), model: m.name }) + ' · ' + fmt(I.cost_tokens, { n: num(cost) });
    }
    show(sub, cost > 0);
    const go = $('aigGo');
    // В правке кнопка называет дело своим именем: человек принёс фото, а не просит нарисовать.
    const edit = S.mode === 'edit' && I.edit_go;
    go.textContent = S.compare ? I.compare_go
        : (edit ? I.edit_go + (S.count > 1 ? ' · ' + S.count : '') : (S.count > 1 ? fmt(I.generate_n, { n: S.count }) : I.generate));
    refreshWallet(cost);
}

function refreshWallet(need) {
    const box = $('aigWallet'), text = $('aigWalletText');
    const topup = $('aigTopup'), login = $('aigLogin'), reg = $('aigRegister');
    [topup, login, reg].forEach((a) => { if (a) a.hidden = true; });
    box.classList.remove('short');
    if (!S.boot) { show(box, false); return; }
    if (!S.authed) {
        text.textContent = (S.mode === 'edit' && I.login_line_edit) || I.login_line || '';
        if (login) login.hidden = false;
        if (reg) reg.hidden = false;
        show(box, true);
        return;
    }
    if (typeof S.balance !== 'number') { show(box, false); return; }
    if (S.balance < need) {
        const short = need - S.balance;
        text.textContent = fmt(I.short_line, { n: num(short) }) + ' ' + fmt(I.short_money, { n: moneyBare(short) });
        box.classList.add('short');
        if (topup) { topup.hidden = false; topup.setAttribute('data-tmb-short', String(short)); }
    } else {
        text.textContent = fmt(I.balance, { n: num(S.balance) }) + ' ' + fmt(I.balance_money, { n: moneyBare(S.balance) });
    }
    show(box, true);
}

/* ─────────────────────────── размер идёт за фото ─────────────────────────────
   Модели рисуют в том формате, который им назвали, а не в формате присланного
   фото. Замер 07.10.2026 по 48 правкам с прода: ни одна выдача не пошла за
   снимком, и в 16 из них форма кадра вышла другой — альбомное фото возвращалось
   квадратом, портрет 3:4 — альбомом 4:3 (человек пять раз подряд переспрашивал
   и дописывал «оставить фото»). Поэтому в режиме правки размер берётся с
   первого фото, пока его не выбрали руками; выбранное руками сильнее. */
function photoAspect() {
    const r = S.refs.find((x) => x.w > 0 && x.h > 0);
    return r ? r.w / r.h : 0;
}
function nearestBasicSize(ratio) {
    const sizes = (S.gen && S.gen.sizes) || {};
    let best = null, bd = Infinity;
    Object.keys(sizes).forEach((k) => {
        if ((sizes[k].group || 'basic') !== 'basic') return;
        const a = String(sizes[k].aspect || '1:1').split(':');
        const d = Math.abs(Math.log(ratio / ((+a[0] || 1) / (+a[1] || 1))));
        if (d < bd) { bd = d; best = k; }
    });
    return best;
}
function syncSizeToPhoto() {
    const ratio = photoAspect();
    if (S.mode === 'edit' && !S.sizeTouched && ratio > 0 && S.gen) {
        const k = nearestBasicSize(ratio);
        if (!k) return;
        if (!S.sizeAuto) S.sizeOwn = S.size;
        S.size = k; S.sizeAuto = true;
    } else if (S.sizeAuto) {
        S.sizeAuto = false;
        if (S.sizeOwn) S.size = S.sizeOwn;
        S.sizeOwn = null;
    }
}

function paintAll() {
    syncSizeToPhoto();
    paintKnobs(); paintTextWarn(); paintChars(); paintRefs(); paintPrice();
    paintModelPop(); paintSizePop(); paintResPop(); paintCountPop(); paintStylePop(); paintBgPop(); paintCompare();
    paintAround(); paintPrintWarn();
}

/* ─────────────────────────── только вокруг фото ──────────────────────────────
   Рамка, паспарту, дорисовка кадра по краям. Порождающая модель перерисовывает
   ВЕСЬ кадр — на «сделать рамку, на фото ничего не менять» Nano Banana 2 вешала
   картину на стену под углом и рисовала лица заново (чат 702, 20.10.2026).
   Поэтому страница ставит снимок на холст с серыми полями, модель рисует в
   полях, а сервер возвращает в результат ИСХОДНЫЕ пиксели снимка
   (AroundPhoto::composite). Пробы: снимок остаётся на месте с точностью 0–4 px
   у Nano Banana 2 и Seedream; сдвиг GPT Image 2.5 сервер находит сам. */
const AROUND_GREY = 128;                                      // = AroundPhoto::GREY
const AROUND_LONG = { '1K': 1400, '2K': 2800, '4K': 5000 };   // ≈ выдача модели в этом разрешении
const AROUND_MAX_PX = 16e6;                                   // потолок холста iOS Safari
const AROUND_CHIPS = { frame: 10, expand: 15 };               // начала просьб, включающие режим, и их поля
const aroundOn = () => S.mode === 'edit' && S.around && S.refs.length > 0;

/* ─────────────────────────── «Совместное фото» ──────────────────────────────
   Люди с РАЗНЫХ фото на одном снимке (отзыв 26.10.2026 на «Объединение фото»:
   «Пусть они стоят вместе в обнимку»). Описание складывает поза — человек
   выбирает её кнопкой, а не пишет промпт. Проба 28.10.2026 (два несуществующих
   человека, разные свет и фон): Nano Banana 2.1 держит обоих — сходство лиц
   SFace с исходниками 0,74–0,88 при пороге «тот же человек» 0,36, лица не
   смешиваются; «Авто» с фото и так выбирает её. */
const POSES = ['hug', 'side', 'hands', 'young'];
const poseText = (k) => I['pose_' + k + '_p'] || '';
/** Начала просьб и описания поз: стоят в поле одни — их можно заменять, а не дописывать. */
function knownLeads() {
    return Object.keys(I).filter((x) => /^etpl_.+_p$/.test(x)).map((x) => I[x]).concat(POSES.map(poseText));
}
function setTogether(k, why) {
    S.together = POSES.includes(k) ? k : null;
    const box = $('aigPoses');
    show(box, S.mode === 'edit' && !!S.together);
    if (box) box.querySelectorAll('[data-pose]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-pose') === S.together)));
    if (!S.together) return;
    const p = $('aigPrompt');
    if (!p.value.trim() || knownLeads().includes(p.value)) { p.value = poseText(S.together); paintChars(); }
    // Совместное фото — это новый снимок, а не рамка вокруг старого.
    S.around = false;
    if (S.boot) paintAll();
    ae('aig_pose', { p: S.together, why: why || '' });
}
function sizeAspect() {
    const a = String(((S.allSizes && S.allSizes[S.size]) || {}).aspect || '1:1').split(':');
    return (+a[0] || 1) / (+a[1] || 1);
}
/** Окно снимка на холсте (доли): по центру, поля — от меньшей стороны холста. */
function aroundRect(pw, ph, aspect, marginPct) {
    const m = (marginPct / 100) * Math.min(aspect, 1);
    const k = Math.min((aspect - 2 * m) / pw, (1 - 2 * m) / ph);
    const w = pw * k, h = ph * k;
    return { x: (aspect - w) / 2 / aspect, y: (1 - h) / 2, w: w / aspect, h: h };
}
/** Поля 0 и формат как у фото — модели негде рисовать, а заплатить было бы за копию снимка. */
function aroundIsEmpty() {
    const r = S.refs[0];
    if (!r || !(r.w > 0 && r.h > 0) || S.margin > 0) return false;
    return Math.abs(Math.log((r.w / r.h) / sizeAspect())) < 0.02;
}
function paintAround() {
    const box = $('aigAround');
    if (!box) return;
    show(box, S.mode === 'edit');
    $('aigAroundOn').checked = !!S.around;
    show($('aigAroundBody'), !!S.around);
    $('aigMargin').value = String(S.margin);
    $('aigMarginV').textContent = S.margin + ' %';
    if (!S.around) return;
    // Превью холста: серые поля — там, где будет рисовать модель.
    const c = $('aigAroundPrev'), x = c.getContext('2d');
    const box132 = c.width, aspect = sizeAspect();
    const W = aspect >= 1 ? box132 : Math.round(box132 * aspect), H = aspect >= 1 ? Math.round(box132 / aspect) : box132;
    x.clearRect(0, 0, box132, box132);
    const ox = (box132 - W) / 2, oy = (box132 - H) / 2;
    x.fillStyle = 'rgb(' + AROUND_GREY + ',' + AROUND_GREY + ',' + AROUND_GREY + ')';
    x.fillRect(ox, oy, W, H);
    const ref = S.refs[0];
    const pw = ref && ref.w > 0 ? ref.w : 3, ph = ref && ref.h > 0 ? ref.h : 4;
    const r = aroundRect(pw, ph, aspect, S.margin);
    const rx = ox + r.x * W, ry = oy + r.y * H, rw = r.w * W, rh = r.h * H;
    const img = ref && aroundThumb(ref);
    if (img && img.complete && img.naturalWidth) x.drawImage(img, rx, ry, rw, rh);
    else { x.fillStyle = 'rgba(255,255,255,.75)'; x.fillRect(rx, ry, rw, rh); }
}
/** Миниатюра первого фото для превью — грузится один раз на фото. */
function aroundThumb(ref) {
    if (ref._thumbImg && ref._thumbImg.dataset.src === ref.thumb) return ref._thumbImg;
    const img = new Image();
    img.dataset.src = ref.thumb;
    img.onload = () => paintAround();
    img.src = ref.thumb;
    ref._thumbImg = img;
    return img;
}
/** Холст для модели: снимок по центру, поля серые; загружается как образец. */
async function prepareAround() {
    const ref = S.refs[0];
    const m = effModel();
    const res = S.compare ? S.cmp.map((s) => resFor(modelOf(s))).sort().pop() : resFor(m);
    const key = [ref.id || ref.thumb, S.size, S.margin, res].join('|');
    if (S.aroundRef && S.aroundRef.key === key) return S.aroundRef;
    const src = ref.blob || await (await fetch(ref.thumb, { credentials: 'same-origin' })).blob();
    const bmp = await createImageBitmap(src);
    const aspect = sizeAspect();
    const rect = aroundRect(bmp.width, bmp.height, aspect, S.margin);
    const dims = (L) => (aspect >= 1 ? [L, Math.round(L / aspect)] : [Math.round(L * aspect), L]);
    let L = AROUND_LONG[res] || AROUND_LONG['1K'];
    let [CW, CH] = dims(L);
    // Снимок не растягиваем сверх его пикселей: разрешение склейки = разрешение фото.
    const up = (rect.w * CW) / bmp.width;
    if (up > 1) [CW, CH] = dims(Math.max(1024, Math.round(L / up)));
    if (CW * CH > AROUND_MAX_PX) { const s = Math.sqrt(AROUND_MAX_PX / (CW * CH)); CW = Math.floor(CW * s); CH = Math.floor(CH * s); }
    const px = { x: Math.round(rect.x * CW), y: Math.round(rect.y * CH), w: Math.round(rect.w * CW), h: Math.round(rect.h * CH) };
    const c = document.createElement('canvas'); c.width = CW; c.height = CH;
    const x = c.getContext('2d');
    x.fillStyle = 'rgb(' + AROUND_GREY + ',' + AROUND_GREY + ',' + AROUND_GREY + ')';
    x.fillRect(0, 0, CW, CH);
    x.imageSmoothingQuality = 'high';
    x.drawImage(bmp, px.x, px.y, px.w, px.h);
    try { bmp.close(); } catch (_) { /* нет close у старых */ }
    let blob = await window.__canvasBlob(c, 'image/jpeg', 0.92);
    // Потолок загрузки — 12 МБ (config ai-image.max_upload_mb).
    if (blob.size > 11.5 * 1048576) blob = await window.__canvasBlob(c, 'image/jpeg', 0.84);
    c.width = c.height = 1;
    const fd = new FormData(); fd.append('image', blob, 'around.jpg');
    const r = await fetch(U.upload, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf(), 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: fd, credentials: 'same-origin' });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok || !j.ref) throw new Error(j.msg || 'upload');
    S.aroundRef = { key: key, id: 'up:' + j.ref, rect: { x: px.x / CW, y: px.y / CH, w: px.w / CW, h: px.h / CH } };
    ae('aig_around_canvas', { w: CW, h: CH, kb: Math.round(blob.size / 1024), m: S.margin });
    return S.aroundRef;
}

/* Размер «для печати» и разрешение, которого на листе не хватит: 1K на A4 — около
   100 точек на дюйм (чат 702: «теперь не пригодно для печати»). */
const RES_LONG = { '1K': 1264, '2K': 2528, '4K': 5056 };   // длинная сторона выдачи 2:3 у Nano Banana 2 (замер 20.10.2026); у 2.1 таблица та же (22.10: 3:4 → 896×1200, 16:9 → 1376×768)
function paintPrintWarn() {
    const w = $('aigPrintWarn');
    if (!w) return;
    const z = (S.allSizes && S.allSizes[S.size]) || {};
    const m = effModel();
    let txt = '';
    if (z.group === 'print' && z.px && m && !S.compare) {
        const long = Math.max(z.px[0], z.px[1]);
        const dpiOf = (r) => Math.round(300 * (RES_LONG[r] || RES_LONG['1K']) / long);
        const better = m.resolutions.filter((r) => dpiOf(r) >= 200);
        if (dpiOf(resFor(m)) < 200 && better.length) txt = fmt(I.print_dpi_warn, { dpi: dpiOf(resFor(m)), res: better.join(' / ') });
    }
    w.textContent = txt;
    show(w, !!txt);
}

/* ─────────────────────────── заметки ─────────────────────────────────────── */
function note(html, actions) {
    const n = $('aigNote'); n.innerHTML = html;
    if (actions && actions.length) {
        const row = document.createElement('div'); row.className = 'tc-actions';
        actions.forEach((a) => {
            const b = document.createElement('button'); b.type = 'button'; b.className = 'tc-btn sm' + (a.ghost ? ' ghost' : '');
            b.textContent = a.label; b.addEventListener('click', a.run); row.appendChild(b);
        });
        n.appendChild(row);
    }
    show(n, true);
    try { n.scrollIntoView({ block: 'nearest' }); } catch (_) { /* старые браузеры */ }
}
const hideNote = () => show($('aigNote'), false);

/* ─────────────────────────── образцы ─────────────────────────────────────── */
async function downscale(file, maxSide) {
    const bmp = await createImageBitmap(file);
    const k = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * k)), h = Math.max(1, Math.round(bmp.height * k));
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    const x = c.getContext('2d'); x.fillStyle = '#fff'; x.fillRect(0, 0, w, h); x.drawImage(bmp, 0, 0, w, h);
    try { bmp.close(); } catch (_) { /* нет close у старых */ }
    const blob = await new Promise((res) => c.toBlob((b) => res(b), 'image/jpeg', 0.92));
    return { blob: blob, w: w, h: h };
}
async function addFiles(files) {
    // Фото может приехать раньше ответа bootstrap (перенос из соседнего тула):
    // без него страница не знает, вошёл ли человек, и приняла бы его за гостя.
    await BOOT;
    const max = refCap() || 14;
    for (const f of files) {
        if (S.refs.length >= max) { note(esc(fmt(I.refs_max, { max: max }))); break; }
        const entry = { id: null, thumb: URL.createObjectURL(f), busy: true };
        S.refs.push(entry); paintAll();
        try {
            const shot = await downscale(f, 2048);
            const blob = shot.blob;
            entry.w = shot.w; entry.h = shot.h;
            paintAll();   // размер идёт за фото сразу, не дожидаясь конца загрузки
            if (!S.authed) {
                /* Гость. Загрузка требует аккаунта, и до 07.10.2026 выбор фото
                   кончался здесь словами «Не удалось загрузить фото. Попробуйте
                   другое» — про фото, с которым всё в порядке. Теперь оно
                   остаётся на странице, цена считается с ним, а строка у
                   кнопки зовёт войти; фото переживает вход (carryPhotoThroughLogin). */
                entry.busy = false; entry.pending = true; entry.blob = blob;
                paintAll(); flashWallet();
                ae('aig_ref_guest');
                continue;
            }
            const fd = new FormData(); fd.append('image', blob, 'ref.jpg');
            const r = await fetch(U.upload, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf(), 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: fd, credentials: 'same-origin' });
            const j = await r.json().catch(() => ({}));
            if (!r.ok || !j.ok || !j.ref) throw new Error(j.msg || 'upload');
            entry.id = 'up:' + j.ref; entry.busy = false;
            ae('aig_ref_add', { src: 'upload' });
        } catch (e) {
            S.refs = S.refs.filter((x) => x !== entry);
            note(esc(e && e.message && e.message !== 'upload' ? e.message : I.refs_upload_fail));
        }
        paintAll();
    }
}
function addResultAsRef(run, v) {
    const m = effModel();
    const max = refCap() || 14;
    if (S.refs.some((r) => r.id === 'gen:' + run.id + ':' + v.n)) return;
    if (S.refs.length >= max) { note(esc(fmt(I.refs_max, { max: max }))); return; }
    S.refs.push({ id: 'gen:' + run.id + ':' + v.n, thumb: v.url + '?inline=1', busy: false, w: v.width || 0, h: v.height || 0 });
    if (S.model !== 'auto' && !(modelOf(S.model) || {}).refs) S.model = 'auto';
    // «Изменить» на готовой картинке — это правка фото: тот же режим, что у своего снимка.
    setMode('edit', 'result');
    $('aigRefsBox').open = true;
    paintAll();
    note(esc(I.edit_ready));
    $('aigPrompt').focus({ preventScroll: true });
    ae('aig_edit');
}

/* ─────────────────────────── запуск ──────────────────────────────────────── */
function requestBody(modelSlug, group) {
    let refs = S.refs.filter((r) => r.id).map((r) => r.id);
    let around = null;
    // «Только вокруг фото»: первым образцом едет холст, остальные — как были
    // (например, образец рамки со своего снимка).
    if (aroundOn() && S.aroundRef) {
        refs = [S.aroundRef.id].concat(S.refs.slice(1).filter((r) => r.id).map((r) => r.id));
        around = S.aroundRef.rect;
    }
    return {
        tool: SLUG,
        prompt: ($('aigPrompt').value || '').trim(),
        text: ($('aigText').value || '').trim(),
        avoid: ($('aigAvoid').value || '').trim(),
        style: S.style, size: S.size, resolution: S.res, count: S.count,
        transparent: !!S.transparent && !around, model: modelSlug,
        refs: refs, around: around,
        consent: !!$('aigConsent').checked,
        group: group || '',
    };
}

/** Мигнуть строкой кошелька: ответ на нажатие, которое упёрлось во вход или баланс. */
function flashWallet() {
    const box = $('aigWallet');
    if (!box) return;
    box.classList.remove('aig-flash');
    void box.offsetWidth;   // перезапуск анимации при повторном нажатии
    box.classList.add('aig-flash');
}
async function generate() {
    // Нажатие, пришедшее, пока страница ещё не знает цен и моделей, раньше
    // молча пропадало (стенд 25.09: тап через 5 с после открытия на медленном
    // ответе bootstrap — ни звука). Теперь человек видит, что его услышали.
    if (!S.boot) { note(esc(I.booting)); S.bootTap = true; return; }
    // Двойной тап — одна генерация: окно от нажатия до ответа сервера закрыто
    // (стенд 25.09: два тапа через 250 мс = два платных прогона). Второй
    // прогон НАРОЧНО, после ответа на первый, по-прежнему доступен.
    if (S.submitting) return;
    hideNote();
    if (S.busy <= 0) goStatus(null);
    if (!S.live) { note(esc(I.coming_soon)); return; }
    const edit = S.mode === 'edit';
    // Правка без фото — это рисование с нуля под чужой вывеской: сначала фото.
    if (edit && !S.refs.length) { note(esc(I.edit_need_photo)); $('aigRefsBox').open = true; ae('aig_edit_nophoto'); return; }
    const prompt = ($('aigPrompt').value || '').trim();
    if (prompt.length < 3) { note(esc((edit && I.edit_prompt_short) || I.prompt_short)); $('aigPrompt').focus({ preventScroll: true }); return; }
    // Гостю ответ — сама строка «Войдите…» с кнопками входа (она уже на
    // экране): вторая плашка с тем же текстом ниже только дублировала её.
    if (!S.authed) { refreshWallet(totalCost()); flashWallet(); ae('aig_guest_tap'); try { $('aigLogin').focus({ preventScroll: true }); } catch (_) { /* не обязательно */ } return; }
    if (S.refs.some((r) => r.busy)) { note(esc(I.refs_uploading)); return; }
    if (S.refs.length && !$('aigConsent').checked) { note(esc(I.need_consent)); return; }
    if (aroundOn() && aroundIsEmpty()) { note(esc(I.around_nothing)); ae('aig_around_empty'); return; }
    if (S.compare && S.cmp.length < 2) { note(esc(fmt(I.compare_pick, { max: (S.gen && S.gen.max_compare) || 3 }))); return; }
    const cost = totalCost();
    if (typeof S.balance === 'number' && S.balance < cost) {
        refreshWallet(cost);
        flashWallet();
        const t = $('aigTopup');
        if (t) { t.hidden = false; try { t.focus({ preventScroll: true }); } catch (_) { /* фокус не обязателен */ } }
        ae('aig_short', { need: cost, have: S.balance });
        return;
    }
    S.submitting = true;
    goStatus('running');   // ответ человеку — в момент тапа, а не после ответа сервера
    const go = $('aigGo');
    go.setAttribute('aria-busy', 'true');
    go.classList.add('is-busy');
    try {
        if (aroundOn()) {
            try { await prepareAround(); } catch (e) {
                goStatus(null);
                note(esc(I.around_failed));
                ae('aig_around_failed', { err: String((e && (e.code || e.message)) || e).slice(0, 60) });
                return;
            }
        }
        await runSubmit();
    }
    finally {
        S.submitting = false;
        go.removeAttribute('aria-busy');
        go.classList.remove('is-busy');
    }
}
async function runSubmit() {
    if (S.compare) {
        const group = 'c' + Date.now().toString(36);
        const run = newRun({ compare: true, group: group, models: S.cmp.slice() });
        ae('aig_generate', { compare: S.cmp.length, count: S.count, size: S.size, style: S.style, refs: refCount(), text: hasText() ? 1 : 0 });
        stage('generate', { compare: S.cmp.length });
        for (const slug of S.cmp) {
            await submit(slug, group, run);
        }
    } else {
        const m = effModel();
        const run = newRun({ compare: false, models: [m ? m.slug : S.model] });
        ae('aig_generate', { model: m ? m.slug : '', auto: S.model === 'auto' ? 1 : 0, count: S.count, size: S.size, res: resFor(m),
            style: S.style, refs: refCount(), text: hasText() ? 1 : 0, tr: S.transparent ? 1 : 0,
            mode: S.mode, size_auto: S.sizeAuto ? 1 : 0, around: aroundOn() ? S.margin : -1 });
        stage('generate', { model: m ? m.slug : '' });
        await submit(S.model, '', run);
    }
}

async function submit(modelSlug, group, run) {
    const col = run.cols[modelSlug === 'auto' ? (effModel() || {}).slug : modelSlug] || Object.values(run.cols)[0];
    S.busy++;
    let r, j;
    try {
        r = await fetch(U.generate, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
            body: JSON.stringify(requestBody(modelSlug, group)), credentials: 'same-origin',
        });
        j = await r.json().catch(() => ({}));
    } catch (_) {
        S.busy--; failCol(col, I.error_network); return;
    }
    if (r.status === 401) { S.busy--; S.authed = false; refreshWallet(totalCost()); failCol(col, I.login_line); return; }
    if (r.status === 402) {
        S.busy--; if (typeof j.balance === 'number') S.balance = j.balance;
        refreshWallet(j.need || totalCost()); failCol(col, fmt(I.short_line, { n: num((j.need || 0) - (j.balance || 0)) })); return;
    }
    if (!r.ok || !j.ok) {
        S.busy--;
        const msg = j.error === 'blocked' ? I.blocked : (j.error === 'disabled' ? I.coming_soon : (j.msg || I.error_generic));
        failCol(col, msg);
        ae('aig_refused', { code: j.code || j.error || r.status });
        return;
    }
    if (typeof j.balance === 'number') { S.balance = j.balance; refreshWallet(totalCost()); }
    col.id = j.id; col.cost = j.cost_tokens; col.t0 = performance.now();
    paintCol(col);
    goStatus('running');
    poll(col, 1);
}

function poll(col, attempt) {
    const wait = attempt < 12 ? 2000 : 3500;
    setTimeout(async () => {
        let j;
        try {
            const r = await fetch(U.status + '/' + col.id, { headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin' });
            j = await r.json();
        } catch (_) {
            if (attempt < 200) poll(col, attempt + 1); else failCol(col, I.fail_timeout);
            return;
        }
        if (j.status === 'done') {
            S.busy = Math.max(0, S.busy - 1);
            col.done = true; col.variants = j.variants || []; col.request = j.request; col.charged = j.charged_tokens; col.model = j.model; col.modelName = j.model_name;
            paintCol(col);
            const ms = Math.round(performance.now() - col.t0);
            ae('aig_done', { model: j.model, ms: ms, got: col.variants.length, want: (j.request || {}).count || 1 });
            // Воронка и счётчик успеха сайта: без них дашборды считали тул
            // «ни разу не доведшим работу до конца» (найдено тест-проходом 25.09).
            stage('delivered', { model: j.model });
            if (S.busy <= 0) goStatus('done');
            try {
                window.__aeToolComplete && window.__aeToolComplete({
                    slug: SLUG, model: j.model, got: col.variants.length, want: (j.request || {}).count || 1,
                    ms: ms, tokens: col.charged || 0, size: (j.request || {}).size || S.size, refs: refCount(),
                });
            } catch (_) { /* телеметрия не валит тул */ }
            loadHistory(true);
            return;
        }
        if (j.status === 'failed') {
            S.busy = Math.max(0, S.busy - 1);
            col.failed = true; col.reason = j.reason || 'generic'; col.model = j.model || col.model;
            paintCol(col);
            ae('aig_fail', { model: j.model, reason: col.reason });
            stage('failed', { reason: col.reason });
            if (S.busy <= 0) goStatus('failed');
            return;
        }
        // Шлюз поставщика не дождался модели — сервер сам отправил запрос ещё раз.
        if (j.retrying && !col.retrying) {
            col.retrying = true;
            paintCol(col);
            ae('aig_vendor_retry', { model: j.model });
        }
        if (attempt < 200) poll(col, attempt + 1); else failCol(col, I.fail_timeout);
    }, wait);
}

/* ─────────────────────────── сцена: прогоны ──────────────────────────────── */
function newRun(o) {
    const req = requestBody('', o.group || '');
    const run = { compare: o.compare, group: o.group || '', req: req, cols: {}, el: document.createElement('div') };
    run.el.className = 'aig-run';
    const head = document.createElement('div'); head.className = 'aig-run-head';
    head.innerHTML = '<b>' + esc(req.prompt.length > 90 ? req.prompt.slice(0, 88) + '…' : req.prompt) + '</b><span>' + esc(sizeLabel(req.size)) + '</span>';
    run.el.appendChild(head);
    const body = document.createElement('div'); body.className = o.compare ? 'aig-cmpgrid' : '';
    run.el.appendChild(body);
    o.models.forEach((slug) => {
        const m = modelOf(slug) || effModel();
        const col = { slug: m ? m.slug : slug, modelName: m ? m.name : slug, count: S.count, el: document.createElement('div'), run: run, variants: [] };
        col.el.className = o.compare ? 'aig-cmpcol' : 'aig-cmpcol';
        run.cols[col.slug] = col;
        body.appendChild(col.el);
        paintCol(col);
    });
    const box = $('aigResults');
    box.insertBefore(run.el, box.firstChild);
    show(box, true);
    show($('aigExamples'), false);
    show($('aigHold'), false);
    $('aigStageTitle').textContent = o.compare ? I.stage_compare : I.stage_results;
    S.runs.unshift(run);
    try { run.el.scrollIntoView({ block: 'nearest' }); } catch (_) { /* старые браузеры */ }
    return run;
}

function failCol(col, msg) {
    if (!col) return;
    col.failed = true; col.msg = msg;
    paintCol(col);
    if (S.busy <= 0) goStatus('failed');
}

/**
 * Строка у кнопки «Создать»: на узком экране результат рисуется НИЖЕ сгиба
 * (iPhone 15: карточка «Рисую…» на 875 px при экране 659), и без неё тап
 * выглядел как «ничего не произошло» — вторая платная генерация следующим
 * тапом. Ссылка, а не автопрокрутка: перейти к результату решает человек.
 */
function goStatus(kind) {
    const a = $('aigGoStatus');
    if (!a) return;
    if (!kind) { a.hidden = true; return; }
    const txt = kind === 'running' ? I.go_running : (kind === 'failed' ? I.go_failed : I.go_done);
    a.textContent = (txt || '') + ' ↓';
    a.classList.toggle('bad', kind === 'failed');
    a.hidden = false;
}

function reasonText(reason) {
    return I['fail_' + (reason || 'generic')] || I.fail_generic;
}

function aspectOf(req) {
    const z = (S.allSizes && S.allSizes[req && req.size]) || {};
    const a = String(z.aspect || '1:1').split(':');
    return (+a[0] || 1) + ' / ' + (+a[1] || 1);
}

function paintCol(col) {
    const el = col.el; el.textContent = '';
    if (col.run.compare) {
        const h = document.createElement('div'); h.className = 'aig-cmpcol-h';
        h.innerHTML = '<b>' + esc(col.modelName) + '</b><span>' + (col.done && col.charged ? esc(fmt(I.charged, { n: num(col.charged) })) : '') + '</span>';
        el.appendChild(h);
    }
    const cards = document.createElement('div');
    cards.className = 'aig-cards' + ((col.done ? col.variants.length : col.count) === 1 && !col.run.compare ? ' cols-1' : '');
    el.appendChild(cards);
    const aspect = aspectOf(col.run.req);

    if (col.failed) {
        const n = document.createElement('div');
        n.className = 'aig-run-note bad';
        n.textContent = col.msg || reasonText(col.reason);
        el.appendChild(n);
        // Другой движок — выход и для отказа без названной причины: 13.10.2026 на
        // «попробуйте ещё раз» покупатель трижды повторил запрос, который тот же
        // движок принять не мог (просьба написать текст вместо описания картинки).
        // «moved» — модель сдвинула фото с холста «только вокруг»: у соседней
        // модели на пробах снимок оставался на месте.
        if (col.reason === 'content' || col.reason === 'busy' || col.reason === 'request' || col.reason === 'generic' || col.reason === 'moved') {
            const other = S.models.find((m) => m.slug !== col.slug && (!refCount() || m.refs > 0) && m.role !== 'draft');
            if (other) {
                const b = document.createElement('button'); b.type = 'button'; b.className = 'tc-btn ghost sm';
                b.textContent = fmt(I.try_model, { model: other.name });
                b.addEventListener('click', () => { S.model = other.slug; S.compare = false; $('aigCompareOn').checked = false; paintAll(); generate(); ae('aig_try_other', { from: col.slug, to: other.slug }); });
                el.appendChild(b);
            }
        }
        return;
    }
    if (!col.done) {
        for (let i = 0; i < col.count; i++) {
            const card = document.createElement('div'); card.className = 'aig-card'; card.style.aspectRatio = aspect;
            const busy = document.createElement('div'); busy.className = 'aig-card-busy';
            const mm = modelOf(col.slug);
            const secs = mm ? ((col.run.req.refs || []).length ? mm.edit_s : mm.gen_s) : 0;
            const eta = secs ? ' · ' + fmt(I.generating_eta, { n: secs < 90 ? secs : Math.round(secs / 60) * 60 }) : '';
            busy.innerHTML = '<div class="aig-spin" aria-hidden="true"></div><b>' + esc(col.retrying ? I.retrying : (col.id ? I.generating : I.queued)) + '</b><span>' + esc(col.modelName + eta) + '</span>';
            card.appendChild(busy); cards.appendChild(card);
        }
        return;
    }
    const want = (col.request && col.request.count) || col.count;
    col.variants.forEach((v, idx) => cards.appendChild(resultCard(col, v, idx)));
    wireMenus(el);
    if (col.variants.length < want) {
        const n = document.createElement('div'); n.className = 'aig-run-note';
        n.textContent = fmt(I.partial, { got: col.variants.length, want: want });
        el.appendChild(n);
    } else if (!col.run.compare && col.charged) {
        const n = document.createElement('div'); n.className = 'aig-run-note';
        n.textContent = col.modelName + ' · ' + fmt(I.charged, { n: num(col.charged) });
        el.appendChild(n);
    }
}

function resultCard(col, v, idx) {
    const card = document.createElement('div'); card.className = 'aig-card';
    const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'aig-card-btn';
    btn.setAttribute('aria-label', I.act_open);
    const img = document.createElement('img'); img.className = 'aig-card-img'; img.alt = (col.request && col.request.prompt) || '';
    img.loading = 'lazy'; img.decoding = 'async'; img.src = v.url + '?inline=1';
    if (v.width && v.height) img.style.aspectRatio = v.width + ' / ' + v.height;
    btn.appendChild(img);
    btn.addEventListener('click', () => openLb(col.variants.map((x) => ({ col: col, v: x })), idx));
    card.appendChild(btn);

    const bar = document.createElement('div'); bar.className = 'aig-card-bar';
    const key = col.id + '_' + v.n;
    bar.appendChild(menuButton('aigDl_' + key, '⬇ ' + I.act_download, 'tc-btn sm', downloadItems(col, v)));
    const ed = document.createElement('button'); ed.type = 'button'; ed.className = 'tc-btn ghost sm'; ed.textContent = '✎';
    ed.title = I.act_edit; ed.setAttribute('aria-label', I.act_edit);
    ed.addEventListener('click', () => addResultAsRef({ id: col.id }, v));
    bar.appendChild(ed);
    const more = menuButton('aigMo_' + key, '⋯', 'tc-btn ghost sm aig-more-btn', moreItems(col, v));
    more.querySelector('button').setAttribute('aria-label', I.act_more);
    bar.appendChild(more);
    card.appendChild(bar);
    return card;
}

/* ─────────────────────────── меню действий ─────────────────────────────────
   Меню карточки — обычный поповер общего js/anchored-popover.js: у каждой
   карточки свой, с id, триггером popovertarget и data-anchor. Размещение,
   закрытие по выбору строки и лестница для браузеров без Popover API — его. */
function menuButton(id, label, cls, items) {
    const wrap = document.createElement('span'); wrap.className = 'aig-mwrap';
    const b = document.createElement('button'); b.type = 'button'; b.className = cls; b.textContent = label;
    b.setAttribute('popovertarget', id);
    const pop = document.createElement('div');
    pop.className = 'aig-pop'; pop.id = id; pop.setAttribute('popover', ''); pop.setAttribute('role', 'menu');
    pop.setAttribute('data-anchor', 'start'); pop.setAttribute('data-pop-open', 'false');
    items.forEach((it) => {
        const r = document.createElement('button'); r.type = 'button'; r.className = 'aig-opt'; r.setAttribute('role', 'menuitem');
        r.innerHTML = '<span class="aig-opt-main"><span class="aig-opt-name">' + esc(it.label) + '</span>'
            + (it.facts ? '<span class="aig-opt-facts">' + esc(it.facts) + '</span>' : '') + '</span>';
        r.addEventListener('click', () => it.run());
        pop.appendChild(r);
    });
    wrap.appendChild(b); wrap.appendChild(pop);
    return wrap;
}
function wireMenus(root) {
    const go = () => { try { window.__anchoredPopover && window.__anchoredPopover.scan(root); } catch (_) { /* меню откроется браузерным поповером */ } };
    requestAnimationFrame(go);
}
function downloadItems(col, v) {
    const req = col.request || {};
    const items = [
        { label: I.act_png, run: () => save(col, v, 'png', null) },
        { label: I.act_jpg, run: () => save(col, v, 'jpg', null) },
        { label: I.act_webp, run: () => save(col, v, 'webp', null) },
    ];
    if (req.target_px && req.target_px.length === 2) {
        const w = req.target_px[0], h = req.target_px[1];
        items.unshift({ label: fmt(I.act_exact, { w: w, h: h, fmt: req.transparent ? 'PNG' : 'JPG' }),
            facts: (v.width && v.height && (v.width < w || v.height < h)) ? fmt(I.exact_upscale_warn, { w: w, h: h }) : '',
            run: () => save(col, v, req.transparent ? 'png' : 'jpg', [w, h]) });
    }
    return items;
}
function moreItems(col, v) {
    const m = modelOf(col.model || col.slug);
    const req = col.request || {};
    const per = m ? (m.prices[req.resolution] || m.prices[m.default_res] || 0) * (req.count || 1) : 0;
    return [
        { label: I.act_vary, facts: per ? money(per) : '', run: () => vary(col) },
        { label: I.act_reuse, run: () => reuse(col) },
        { label: I.act_copy, run: () => copyPrompt(req.prompt || '') },
        { label: I.act_upscale, run: () => carryTo(U.upscale, col, v, 'upscale') },
        { label: I.act_bg, run: () => carryTo(U.bg, col, v, 'bg') },
    ].concat(navigator.share ? [{ label: I.act_share, run: () => share(col, v) }] : []);
}

function fileBase(req) {
    const words = String((req && req.prompt) || 'image').split(/\s+/).slice(0, 7).join(' ');
    const N = window.__outputName;
    return N && N.sanitize ? (N.sanitize(words) || 'image') : words.replace(/[\\/:*?"<>|]+/g, '').trim() || 'image';
}

/* Скачивание варианта (23.10.2026, Премиум u 3341, Opera 137, gpt-image 4K: три нажатия «PNG» на
   файл 2880×2880 / 11 МБ — три тоста «Не получилось запустить генерацию», файла нет).
   Было: файл, который УЖЕ лежит на нашем сервере в нужном формате (и уже с ИИ-отметкой —
   AiContentMarker, а у файлов с C2PA — их манифест), страница скачивала целиком в Blob
   (11–14 МБ, после трёх с лишним секунд ожидания), переписывала его ещё раз в памяти меткой
   ai-mark и только потом отдавала двери скачивания — уже без жеста нажатия. На этом пути у
   этого браузера и упало (сервер отдал файл полностью, rt 3,4 с), а catch называл любую беду
   фразой о ГЕНЕРАЦИИ и не говорил, что случилось. Теперь: тот же формат — файл сервера
   ссылкой, в самом нажатии (__downloadLink: без Blob в странице, без await, жест не теряется,
   C2PA не ломается вставкой tEXt перед ним); перекодирование (JPG/WebP/точный размер) —
   картинка через <img> (без Blob на весь файл), кодирование через __canvasBlob, холст в
   потолке браузера (canvasLimits); отказ — своей фразой и с этапом в событии. */
const MIME = { png: 'image/png', jpg: 'image/jpeg', webp: 'image/webp' };
const sameMime = (a, b) => !!a && (a === b || (b === 'image/jpeg' && a === 'image/jpg'));
function loadImage(url) {
    return new Promise((resolve, reject) => {
        const im = new Image();
        im.decoding = 'async';
        im.onload = () => (im.decode ? im.decode().catch(() => {}) : Promise.resolve()).then(() => resolve(im));
        im.onerror = () => reject(new Error('image load failed'));
        im.src = url;
    });
}
async function save(col, v, format, exact) {
    const req = col.request || {};
    ae('aig_download', { fmt: format, exact: exact ? 1 : 0, via: (!exact && sameMime(v.mime, MIME[format])) ? 'link' : 'canvas' });
    const mime = MIME[format];
    const name = fileBase(req) + '-' + col.id + '-' + v.n + (exact ? '-' + exact[0] + 'x' + exact[1] : '') + '.' + format;
    if (!exact && sameMime(v.mime, mime) && typeof window.__downloadLink === 'function') {
        window.__downloadLink(v.url, name);
        return;
    }
    let stage = 'decode';
    try {
        let blob = null;
        const img = await loadImage(v.url + '?inline=1');
        {
            let sw = img.naturalWidth, sh = img.naturalHeight, sx = 0, sy = 0, W = sw, H = sh;
            // Куда ляжет картинка: обычно — обрезка по центру до точного формата;
            // у картинки «только вокруг фото» по краю идёт рамка, и обрезка срезала
            // бы её верх и низ (2:3 → A4 — по 2,6 %), поэтому она вписывается с
            // белыми полями, как на листе из принтера.
            let dx = 0, dy = 0, dw = W, dh = H;
            if (exact) {
                W = exact[0]; H = exact[1]; dw = W; dh = H;
                const want = W / H, have = sw / sh;
                if (req.around) {
                    if (have > want) { dh = Math.round(W / have); dy = Math.round((H - dh) / 2); }
                    else if (have < want) { dw = Math.round(H * have); dx = Math.round((W - dw) / 2); }
                } else if (have > want) { const nw = Math.round(sh * want); sx = Math.round((sw - nw) / 2); sw = nw; }
                else if (have < want) { const nh = Math.round(sw / want); sy = Math.round((sh - nh) / 2); sh = nh; }
            }
            // Холст не больше того, что этот браузер правда заполнит (старый iOS — 16,7 Мп):
            // за потолком он молча белый. Тогда меньше, но целиком, а не пустой файл.
            const lim = canvasLimits();
            const k = Math.min(1, Math.sqrt(lim.area / (W * H)), lim.side / Math.max(W, H));
            if (k < 1) { W = Math.floor(W * k); H = Math.floor(H * k); dx = Math.floor(dx * k); dy = Math.floor(dy * k); dw = Math.floor(dw * k); dh = Math.floor(dh * k); }
            stage = 'draw';
            const c = document.createElement('canvas'); c.width = W; c.height = H;
            const x = c.getContext('2d');
            if (format === 'jpg' || (exact && req.around)) { x.fillStyle = '#fff'; x.fillRect(0, 0, W, H); }
            x.imageSmoothingQuality = 'high';
            x.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
            stage = 'encode';
            blob = await window.__canvasBlob(c, mime, format === 'png' ? undefined : 0.93);
            c.width = c.height = 0;
        }
        stage = 'mark';
        if (window.__aiMark && window.__aiMark.auto) {
            try { blob = await window.__aiMark.auto(blob, name, SLUG); } catch (_) { /* метка не должна отнимать файл */ }
        }
        stage = 'deliver';
        if (window.__downloadFile) await window.__downloadFile(blob, name);
    } catch (e) {
        ae('aig_download_failed', { fmt: format, exact: exact ? 1 : 0, stage: stage,
            err_name: String((e && (e.code || e.name)) || 'Error').slice(0, 40), err_msg: String((e && e.message) || e || '').slice(0, 160) });
        if (window.__toast) window.__toast(I.download_failed, 'error');
    }
}

async function share(col, v) {
    try {
        const r = await fetch(v.url + '?inline=1', { credentials: 'same-origin' });
        const b = await r.blob();
        const f = new File([b], fileBase(col.request) + '.' + (b.type === 'image/png' ? 'png' : 'jpg'), { type: b.type });
        await navigator.share({ files: [f] });
        ae('aig_share');
    } catch (_) { /* отмена шаринга — не ошибка */ }
}

function copyPrompt(text) {
    try {
        navigator.clipboard.writeText(text).then(() => { window.__toast && window.__toast(I.act_copied, 'success'); });
    } catch (_) { /* буфер недоступен */ }
}

function fillFrom(req, modelSlug) {
    if (!req) return;
    // Настройки прежнего запуска несут и его режим: «Ещё варианты» у нарисованной
    // с нуля картинки не должны упереться в «сначала добавьте фото».
    if (typeof req.refs === 'number') setMode(req.refs > 0 ? 'edit' : 'draw', 'reuse');
    $('aigPrompt').value = req.prompt || '';
    $('aigText').value = req.text || '';
    $('aigAvoid').value = req.avoid || '';
    if (req.size) S.size = req.size;
    if (req.resolution) S.res = req.resolution;
    if (req.count) S.count = req.count;
    if (req.style) S.style = req.style;
    S.transparent = !!req.transparent;
    S.around = !!req.around;
    if (modelSlug && modelOf(modelSlug)) S.model = req.auto ? 'auto' : modelSlug;
    S.compare = false; $('aigCompareOn').checked = false;
    paintAll();
}
function reuse(col) { fillFrom(col.request, col.model); $('aigPrompt').focus({ preventScroll: true }); ae('aig_reuse'); }
function vary(col) {
    const req = col.request || {};
    const keepRefs = S.refs.slice();
    fillFrom(req, col.model);
    if (!req.refs) S.refs = [];
    else S.refs = keepRefs;
    ae('aig_vary', { model: col.model });
    generate();
}

/* ─────────────────────────── просмотр ────────────────────────────────────── */
function openLb(list, idx) {
    S.lbList = list; S.lbIdx = idx;
    paintLb();
    const d = $('aigLb');
    try { d.showModal(); } catch (_) { d.setAttribute('open', ''); }
    ae('aig_open');
}
function paintLb() {
    const it = S.lbList[S.lbIdx]; if (!it) return;
    $('aigLbImg').src = it.v.ex ? it.v.url : it.v.url + '?inline=1';
    show($('aigLbDl'), !it.v.ex); show($('aigLbEdit'), !it.v.ex);
    $('aigLbImg').alt = (it.col.request && it.col.request.prompt) || '';
    $('aigLbCap').textContent = ((it.col.request && it.col.request.prompt) || '') + (it.col.modelName ? ' — ' + it.col.modelName : '');
    show($('aigLbPrev'), S.lbList.length > 1);
    show($('aigLbNext'), S.lbList.length > 1);
}
function lbStep(d) { if (!S.lbList.length) return; S.lbIdx = (S.lbIdx + d + S.lbList.length) % S.lbList.length; paintLb(); }

/* ─────────────────────────── лента «Ваши картинки» ───────────────────────── */
async function loadHistory(reset) {
    if (!S.authed) return;
    if (reset) { S.histBefore = 0; S.histDone = false; $('aigHist').textContent = ''; }
    if (S.histDone) return;
    let j;
    try {
        const q = '?tool=' + SLUG + '&limit=24' + (S.histBefore ? '&before=' + S.histBefore : '');
        const r = await fetch(U.history + q, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        j = await r.json();
    } catch (_) { return; }
    const items = (j && j.items) || [];
    const box = $('aigHist');
    items.forEach((g) => {
        if (g.status !== 'done' || !(g.variants || []).length) return;
        const col = { id: g.id, request: g.request, model: g.model, modelName: g.model_name, variants: g.variants, run: { compare: false, req: g.request }, charged: g.charged_tokens };
        g.variants.forEach((v, i) => {
            const b = document.createElement('button'); b.type = 'button'; b.className = 'aig-hitem';
            b.setAttribute('aria-label', (g.request && g.request.prompt) || I.act_open);
            const im = document.createElement('img'); im.alt = ''; im.loading = 'lazy'; im.decoding = 'async'; im.src = v.url + '?inline=1';
            b.appendChild(im);
            b.addEventListener('click', () => openLb(col.variants.map((x) => ({ col: col, v: x })), i));
            box.appendChild(b);
        });
        S.histBefore = g.id;
    });
    if (items.length < 24) S.histDone = true;
    show($('aigHistMore'), !S.histDone);
    show($('aigHistWrap'), box.children.length > 0);
}

/* ─────────────────────────── режим: нарисовать / изменить фото ──────────────
   Одна страница — два дела. Правка своего фото словами давала 58 запусков из
   108 (07.10.2026), а вывеска говорила только «Что нарисовать», и место для
   фото было свёрнутым ящиком «необязательно». Режим ничего не меняет в запросе:
   он ставит фото первым, называет поле своим именем, предлагает начала просьб
   для правки и отпускает размер за снимком (syncSizeToPhoto). */
function setMode(mode, why) {
    mode = mode === 'edit' ? 'edit' : 'draw';
    if (S.mode === mode && S.modeApplied) return;
    const changed = S.mode !== mode;
    S.mode = mode;
    // Вход в правку — размер снова идёт за фото: выбранный для рисунка «Сторис»
    // не должен перекроить чужой снимок. Выбор, сделанный уже в правке, держится.
    if (mode === 'edit' && changed) S.sizeTouched = false;
    applyMode();
    if (changed) { savePref('mode', mode); ae('aig_mode', { m: mode, why: why || '' }); }
}
function applyMode() {
    S.modeApplied = true;
    const edit = S.mode === 'edit';
    const comp = $('aigComposer'), box = $('aigRefsBox');
    comp.classList.toggle('is-edit', edit);
    document.querySelectorAll('#aigModes .tc-seg-b').forEach((b) => {
        const on = b.getAttribute('data-mode') === S.mode;
        b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    $('aigPromptLbl').textContent = (edit && I.edit_prompt_label) || I.prompt_label;
    $('aigPrompt').placeholder = (edit && I.edit_prompt_ph) || I.prompt_ph;
    $('aigRefsTitle').textContent = (edit && I.edit_refs_title) || I.refs_title;
    $('aigRefsHint').textContent = (edit && I.edit_refs_hint) || I.refs_hint;
    show($('aigIdea'), !edit);
    show($('aigTplsDraw'), !edit);
    show($('aigTplsEdit'), edit);
    show($('aigPoses'), edit && !!S.together);
    // Фото — первым в правке, после надписи в рисовании. Узел переезжает в
    // разметке, а не через order: порядок табуляции обязан совпадать с видимым.
    if (edit) { comp.insertBefore(box, $('aigPromptBox')); box.open = true; }
    else comp.insertBefore(box, $('aigKnobs'));
    if (S.boot) { paintExamples(); paintAll(); }
}

/* ─────────────────────────── двери в соседние тулы ───────────────────────────
   «Увеличить в 4 раза» и «Убрать фон» вели на соседнюю страницу БЕЗ картинки:
   человек приезжал к пустой зоне загрузки. Теперь файл едет общим переносом
   (tool-handoff.js); не вышло положить — обычный переход, как раньше. */
async function carryTo(url, col, v, to) {
    ae('aig_handoff', { to: to });
    const th = window.__toolHandoff;
    try {
        if (!th || typeof th._put !== 'function' || typeof th.available !== 'function' || !th.available()) throw new Error('no-transport');
        const r = await fetch(v.url + '?inline=1', { credentials: 'same-origin' });
        if (!r.ok) throw new Error('fetch');
        const blob = await r.blob();
        await th._put(blob, fileBase(col.request) + '.' + (blob.type === 'image/png' ? 'png' : 'jpg'), SLUG, null);
        location.href = url + (url.indexOf('?') === -1 ? '?' : '&') + 'tmb_in=1';
    } catch (_) {
        location.href = url;
    }
}

/** Фото гостя переживает вход: кладём его в перенос и возвращаемся с ним. */
function carryPhotoThroughLogin(a) {
    if (!a) return;
    a.addEventListener('click', (e) => {
        const p = S.refs.find((r) => r.pending && r.blob);
        const th = window.__toolHandoff;
        if (!p || !th || typeof th._put !== 'function' || typeof th.available !== 'function' || !th.available()) return;
        e.preventDefault();
        const here = encodeURIComponent(location.pathname);
        const href = a.href.split(here).join(encodeURIComponent(location.pathname + '?tmb_in=1'));
        ae('aig_guest_carry');
        th._put(p.blob, 'photo.jpg', SLUG, { mode: 'edit' }).catch(() => {}).then(() => { location.href = href; });
    });
}

/* ─────────────────────────── примеры ─────────────────────────────────────── */
function paintExamples() {
    const grid = $('aigExGrid'); grid.textContent = '';
    // Примеры своего режима — первыми: в правке человек сначала видит «до и после».
    const list = (CFG.examples || []).slice().sort((a, b) => {
        const ea = !!a.edit === (S.mode === 'edit') ? 0 : 1, eb = !!b.edit === (S.mode === 'edit') ? 0 : 1;
        return ea - eb;
    });
    if (!list.length) { show($('aigExamples'), false); show($('aigHold'), !S.runs.length); return; }
    show($('aigHold'), false);
    list.forEach((ex) => {
        const m = modelOf(ex.model);
        if (!m) return;
        const card = document.createElement('div'); card.className = 'aig-ex';
        const img = document.createElement('img'); img.alt = ex.prompt || ''; img.loading = 'lazy'; img.decoding = 'async'; img.src = ex.thumb || ex.src;
        if (ex.transparent) img.className = 'aig-ex-alpha';
        const meta = document.createElement('div'); meta.className = 'aig-ex-meta';
        meta.innerHTML = '<b>' + esc(m.name + (ex.edit && I.ex_before_after ? ' · ' + I.ex_before_after : '')) + '</b><span>' + esc(ex.prompt || '') + '</span>';
        const b = document.createElement('button'); b.type = 'button'; b.className = 'tc-btn ghost sm'; b.textContent = I.examples_use;
        b.addEventListener('click', () => {
            if (ex.edit) {
                // Пример правки повторяют на СВОЁМ фото: подставляем просьбу и зовём добавить снимок.
                setMode('edit', 'example');
                $('aigPrompt').value = ex.prompt || '';
                // Пример, где формат результата другой, чем у фото (расширение кадра),
                // несёт и свой размер: за снимком расширять было бы некуда.
                if (ex.resize && ex.size) { S.size = ex.size; S.sizeTouched = true; S.sizeAuto = false; S.sizeOwn = null; }
                // Остальные примеры правят фото в его же формате: размер снова идёт за снимком,
                // даже если перед этим его поставил пример расширения.
                else S.sizeTouched = false;
                // Пример рамки сделан «только вокруг фото» — повтор включает тот же режим и поля.
                S.around = !!ex.around;
                if (ex.around) S.margin = +ex.around;
                // Пример «Совместного фото» открывает и позы — следующая проба в один клик.
                if (ex.together) setTogether(ex.together, 'example'); else setTogether(null);
                paintAll();
                if (!S.refs.length) note(esc(I.edit_need_photo));
                ae('aig_example_use', { model: ex.model, edit: 1 });
                $('aigPrompt').focus({ preventScroll: true });
                return;
            }
            setMode('draw', 'example');
            fillFrom({ prompt: ex.prompt || '', text: ex.text || '', size: ex.size || 'square', style: ex.style || 'none', count: 1,
                resolution: ex.resolution || m.default_res, transparent: !!ex.transparent }, ex.model);
            ae('aig_example_use', { model: ex.model });
            $('aigPrompt').focus({ preventScroll: true });
        });
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', () => openLb([{ col: { request: { prompt: ex.prompt }, modelName: m.name, id: 0 }, v: { url: ex.src, n: 0, ex: true } }], 0));
        card.appendChild(img); card.appendChild(meta); card.appendChild(b);
        grid.appendChild(card);
    });
}

/* ─────────────────────────── bootstrap ───────────────────────────────────── */
/* Первый ответ сервера — модели, цены, вошёл ли человек. Одна неудача (обрыв
   на мобильной сети, 502 под нагрузкой) оставляла страницу «выключенной» до
   перезагрузки: без моделей нет ни цены, ни примеров, размер не идёт за фото, а
   фото вошедшего читается как фото гостя (07.10.2026). Поэтому три попытки. */
async function fetchBoot() {
    for (let i = 0; i < 3; i++) {
        try {
            const r = await fetch(U.bootstrap, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
            if (r.ok) return await r.json();
        } catch (_) { /* сеть — следующая попытка */ }
        if (i < 2) await new Promise((res) => setTimeout(res, 1200 * (i + 1)));
    }
    ae('aig_boot_failed');
    return { enabled: false, authed: false, generator: null };
}
async function bootstrap() {
    S.boot = await fetchBoot();
    S.gen = S.boot.generator || null;
    S.models = (S.gen && S.gen.models) || [];
    // Все размеры — для чтения истории (картинка, сделанная на другой локали,
    // скачивается в свой точный размер), видимые — только площадки этой страны.
    S.allSizes = (S.gen && S.gen.sizes) || {};
    if (S.gen && Array.isArray(CFG.sizes)) {
        const vis = {};
        CFG.sizes.forEach((k) => { if (S.allSizes[k]) vis[k] = S.allSizes[k]; });
        S.gen.sizes = vis;
    }
    S.authed = !!S.boot.authed;
    S.balance = typeof S.boot.balance === 'number' ? S.boot.balance : null;
    S.live = !!(S.boot.enabled && S.gen && S.gen.live);
    if (!S.gen || !S.gen.sizes || !S.gen.sizes[S.size]) S.size = (S.gen && S.gen.default_size) || 'square';
    if (S.model !== 'auto' && !modelOf(S.model)) S.model = 'auto';
    paintExamples();
    paintAll();
    loadHistory(true);
    ae('aig_view', { authed: S.authed ? 1 : 0, live: S.live ? 1 : 0 });
    // Ранний тап получил «загружаю…» — теперь цены на экране, кнопка живая.
    // Генерацию за человека не запускаем: это платное действие, и цену он
    // должен увидеть до него.
    if (S.bootTap) { S.bootTap = false; hideNote(); ae('aig_boot_tap'); }
    // Курс денег приезжает отдельным скриптом — перерисовать цены, когда придёт.
    let left = 100;
    const waitRate = () => {
        const tp = window.__tokenPrice;
        if (tp && tp.ready) { tp.ready().then(() => paintAll()).catch(() => {}); return; }
        if (--left > 0) setTimeout(waitRate, 200);
    };
    waitRate();
}

/* ─────────────────────────── события ─────────────────────────────────────── */
function wire() {
    $('aigPrompt').addEventListener('input', () => { paintChars(); });
    $('aigText').addEventListener('input', () => { paintKnobs(); paintTextWarn(); paintPrice(); paintModelPop(); });
    $('aigGo').addEventListener('click', generate);
    const gs = $('aigGoStatus');
    if (gs) gs.addEventListener('click', () => ae('aig_go_status_click'));
    $('aigIdea').addEventListener('click', () => {
        const ideas = CFG.ideas || [];
        if (!ideas.length) return;
        const cur = $('aigPrompt').value;
        let pick = ideas[Math.floor(Math.random() * ideas.length)];
        if (pick === cur && ideas.length > 1) pick = ideas[(ideas.indexOf(pick) + 1) % ideas.length];
        $('aigPrompt').value = pick; paintChars(); ae('aig_idea');
    });
    document.querySelectorAll('.aig-tpl').forEach((b) => b.addEventListener('click', () => {
        const k = b.getAttribute('data-tpl');
        const tpl = (CFG.templates || {})[k] || {};
        const p = $('aigPrompt');
        const lead = I['tpl_' + k + '_p'] || '';
        if (!p.value.trim()) p.value = lead;
        else if (lead && !p.value.startsWith(lead)) p.value = lead + p.value;
        if (tpl.size) S.size = tpl.size;
        if (tpl.style) S.style = tpl.style;
        paintAll();
        p.focus({ preventScroll: true });
        try { p.setSelectionRange(p.value.length, p.value.length); } catch (_) { /* не у всех полей */ }
        ae('aig_template', { t: k });
    }));
    // Начала просьб правки: ставят только слова — размер и стиль остаются за фото.
    document.querySelectorAll('.aig-tpl[data-etpl]').forEach((b) => b.addEventListener('click', () => {
        const k = b.getAttribute('data-etpl');
        if (k === 'together') {
            setTogether(S.together || 'hug', 'chip');
            ae('aig_template', { t: 'edit_together' });
            $('aigPrompt').focus({ preventScroll: true });
            return;
        }
        setTogether(null);
        const p = $('aigPrompt');
        const lead = I['etpl_' + k + '_p'] || '';
        const known = knownLeads();
        // Уже стоит начало другой просьбы и больше ничего — заменяем, а не копим.
        if (!p.value.trim() || known.includes(p.value)) p.value = lead;
        else if (lead && !p.value.startsWith(lead)) p.value = lead + p.value;
        // Рамка и дорисовка — работа ВОКРУГ фото, остальные начала правят сам снимок.
        S.around = k in AROUND_CHIPS;
        if (S.around) S.margin = AROUND_CHIPS[k];
        paintAll();
        p.focus({ preventScroll: true });
        try { p.setSelectionRange(p.value.length, p.value.length); } catch (_) { /* не у всех полей */ }
        ae('aig_template', { t: 'edit_' + k });
    }));
    document.querySelectorAll('#aigPoses [data-pose]').forEach((b) => b.addEventListener('click', () => {
        setTogether(b.getAttribute('data-pose'), 'pose');
    }));
    $('aigAroundOn').addEventListener('change', (e) => {
        S.around = !!e.target.checked;
        paintAll();
        ae('aig_around', { on: S.around ? 1 : 0, why: 'switch' });
    });
    $('aigMargin').addEventListener('input', (e) => {
        S.margin = Math.max(0, Math.min(30, parseInt(e.target.value, 10) || 0));
        savePref('margin', S.margin);
        paintAround();
    });
    document.querySelectorAll('#aigModes .tc-seg-b').forEach((b) => b.addEventListener('click', () => setMode(b.getAttribute('data-mode'), 'tab')));
    // В правке блок фото не сворачивается: нажатие на заголовок не должно спрятать то, ради чего человек здесь.
    const refsHead = $('aigRefsBox').querySelector('summary');
    if (refsHead) refsHead.addEventListener('click', (e) => { if (S.mode === 'edit') e.preventDefault(); });
    carryPhotoThroughLogin($('aigLogin'));
    carryPhotoThroughLogin($('aigRegister'));
    /* Кто пришёл за правкой фото, тот в ней и окажется: якорь #edit (ссылки из
       соседних тулов и из переписки) и фото, приехавшее переносом. Событие
       переноса может случиться раньше этого модуля — тогда читаем оставленную
       им запись. */
    const arrive = (d) => {
        if (!d) return;
        setMode('edit', 'handoff');
        // Из «Объединения фото»: снимки людей приехали пачкой — встречаем позой.
        if (d.hints && d.hints.tpl === 'together') setTogether(POSES.includes(d.hints.pose) ? d.hints.pose : 'hug', 'handoff');
        const hint = d.hints && typeof d.hints.prompt === 'string' ? d.hints.prompt.slice(0, 300) : '';
        if (hint && !$('aigPrompt').value.trim()) { $('aigPrompt').value = hint; paintChars(); }
        // Из «Рамок для фото»: рамку рисуют ВОКРУГ снимка, сам снимок не трогают.
        const around = d.hints ? parseInt(d.hints.around, 10) : NaN;
        if (around >= 0 && around <= 30) { S.around = true; S.margin = around; if (S.boot) paintAll(); }
    };
    document.addEventListener('tool-handoff:arrived', (e) => arrive(e.detail));
    if (window.__toolHandoffArrival) arrive(window.__toolHandoffArrival);
    const fromHash = () => { if (location.hash === '#edit') setMode('edit', 'hash'); else if (location.hash === '#draw') setMode('draw', 'hash'); };
    window.addEventListener('hashchange', fromHash);
    fromHash();
    // Дверь «Совместное фото» из «Объединения фото» несёт шаблон и в адресе:
    // фото могли не доехать (старый браузер, нет места), а поза — должна.
    try {
        const qp = new URLSearchParams(location.search);
        if (qp.get('tpl') === 'together') { setMode('edit', 'link'); setTogether(POSES.includes(qp.get('pose')) ? qp.get('pose') : 'hug', 'link'); }
    } catch (_) { /* старый браузер без URLSearchParams: просто режим по якорю */ }
    applyMode();
    $('aigCompareOn').addEventListener('change', (e) => {
        S.compare = !!e.target.checked;
        if (S.compare && S.cmp.length === 0) {
            const e1 = effModel();
            S.cmp = [e1 ? e1.slug : null, ...S.models.filter((m) => !e1 || m.slug !== e1.slug).filter((m) => m.role !== 'draft').map((m) => m.slug)]
                .filter(Boolean).slice(0, 2);
        }
        ae('aig_compare', { on: S.compare ? 1 : 0 });
        paintAll();
    });
    $('aigHistMore').addEventListener('click', () => loadHistory(false));
    const up = $('aigUploader');
    if (up) {
        // Через примитив, а не голый addEventListener: он отдаёт и файл,
        // выбранный ДО того, как модуль успел подписаться (UploaderListenerRaceTest).
        window.__bindUploaderFile(up, (file, d) => {
            d = d || {};
            const files = (d.files && d.files.length) ? Array.from(d.files) : (file ? [file] : []);
            addFiles(files.filter((f) => /^image\//.test(f.type || '')));
        });
    }
    $('aigLbPrev').addEventListener('click', () => lbStep(-1));
    $('aigLbNext').addEventListener('click', () => lbStep(1));
    $('aigLbClose').addEventListener('click', () => { try { $('aigLb').close(); } catch (_) { $('aigLb').removeAttribute('open'); } });
    $('aigLbDl').addEventListener('click', () => { const it = S.lbList[S.lbIdx]; if (it) save(it.col, it.v, 'png', null); });
    // Stand hook (tests/playwright/ai-image-download-without-a-page-blob.mjs): the
    // format menu lives only on this session's cards; the stand saves the variant
    // open in the lightbox in another format through the same save(). Harmless.
    window.__aigSaveForStand = (fmt) => { const it = S.lbList[S.lbIdx]; return it ? save(it.col, it.v, fmt, null) : null; };
    $('aigLbEdit').addEventListener('click', () => {
        const it = S.lbList[S.lbIdx]; if (!it) return;
        try { $('aigLb').close(); } catch (_) { /* уже закрыт */ }
        addResultAsRef({ id: it.col.id }, it.v);
    });
    $('aigLb').addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') lbStep(document.dir === 'rtl' ? 1 : -1);
        if (e.key === 'ArrowRight') lbStep(document.dir === 'rtl' ? -1 : 1);
    });
}

loadPrefs();
BOOT = bootstrap();
wire();
